import React, { useState } from 'react';
import { CheckCircle, ArrowRight, ArrowLeft, Zap, Users, TrendingUp, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  {
    id: 'intro',
    title: '¿Listo para generar ingresos con Neuralia369?',
    subtitle: 'Responde 4 preguntas rápidas y te contactamos en menos de 60 segundos.',
  },
  {
    id: 'contact',
    title: '¿Cómo te llamamos?',
    subtitle: 'Solo necesitamos tu nombre y cómo contactarte.',
  },
  {
    id: 'interest',
    title: '¿Qué te interesa más?',
    subtitle: 'Elige la opción que mejor describe tu situación.',
  },
  {
    id: 'budget',
    title: '¿Con qué inversión inicial cuentas?',
    subtitle: 'Esto nos ayuda a darte la opción más adecuada.',
  },
  {
    id: 'urgency',
    title: '¿Cuándo quieres empezar?',
    subtitle: 'Sabemos que el tiempo es valioso.',
  },
];

const INTEREST_OPTIONS = [
  { value: 'distribuidor', label: 'Quiero ser Partner / Distribuidor', desc: 'Generar ingresos recurrentes', emoji: '🚀' },
  { value: 'cliente', label: 'Quiero el producto / servicio', desc: 'Mejorar mis resultados personales', emoji: '🎯' },
  { value: 'ambos', label: 'Ambas cosas', desc: 'Empezar como cliente y crecer como partner', emoji: '⭐' },
];

const BUDGET_OPTIONS = [
  { value: 'alto', label: 'Más de €500', emoji: '💎' },
  { value: 'medio', label: '€100 – €500', emoji: '✅' },
  { value: 'bajo', label: 'Menos de €100', emoji: '🌱' },
];

const URGENCY_OPTIONS = [
  { value: 'alta', label: 'Ahora mismo', emoji: '⚡' },
  { value: 'media', label: 'Esta semana', emoji: '📅' },
  { value: 'baja', label: 'El próximo mes', emoji: '🕐' },
];

const STATS = [
  { icon: Users, value: '+200', label: 'Partners activos' },
  { icon: TrendingUp, value: '€2.4K', label: 'Ingreso promedio/mes' },
  { icon: Star, value: '4.9/5', label: 'Satisfacción' },
  { icon: Zap, value: '<60s', label: 'Respuesta garantizada' },
];

export default function UnirsePage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', country: 'España',
    interest: '', budget: '', urgency: '', source: 'quiz-unirse',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const canNext = () => {
    if (step === 1) return form.name.trim().length >= 2 && (form.phone.trim() || form.email.trim());
    if (step === 2) return !!form.interest;
    if (step === 3) return !!form.budget;
    if (step === 4) return !!form.urgency;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/mlm/public/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al enviar');
      setResult(data);
      // Meta Pixel — Lead capturado
      if (window.fbq) {
        window.fbq('track', 'Lead', { content_name: data.bucket, value: data.score, currency: 'EUR' });
      }
    } catch (e) {
      setError(e.message || 'Error de conexión. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const progressPct = step === 0 ? 0 : Math.round((step / 4) * 100);

  if (result) {
    const isHot = result.bucket === 'caliente';
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${isHot ? 'bg-emerald-500/20' : 'bg-blue-500/20'}`}>
            <CheckCircle className={`w-10 h-10 ${isHot ? 'text-emerald-400' : 'text-blue-400'}`} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {isHot ? '¡Perfecto! Te llamo ahora.' : '¡Gracias por tu interés!'}
            </h1>
            <p className="text-slate-400 text-lg">{result.message}</p>
          </div>
          {isHot && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4">
              <p className="text-emerald-400 font-semibold text-sm">
                Tu puntuación: {result.score}/100 — Perfil caliente
              </p>
              <p className="text-slate-400 text-sm mt-1">
                Un asesor de Neuralia369 te contactará en los próximos 60 segundos.
              </p>
            </div>
          )}
          <div className="space-y-3">
            <a
              href="https://wa.me/34653675515?text=Hola,%20acabo%20de%20completar%20el%20quiz%20de%20Neuralia369"
              className="block w-full py-3 px-6 bg-[#25D366] text-white rounded-xl font-semibold hover:bg-[#20bc58] transition-colors"
              target="_blank" rel="noopener noreferrer"
            >
              Escribir por WhatsApp ahora
            </a>
            <a href="/" className="block text-slate-500 text-sm hover:text-slate-300 transition-colors">
              Volver al inicio
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] flex flex-col">
      {/* Header */}
      <div className="bg-[#030712]/80 border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <a href="/" className="text-white font-bold text-lg tracking-tight">
          Neuralia<span className="text-[#00d4ff]">369</span>
        </a>
        {step > 0 && (
          <div className="flex items-center gap-3">
            <div className="w-32 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#00d4ff] to-[#6366f1] rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-slate-500 text-xs">{step}/4</span>
          </div>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">

          {/* PASO 0 — INTRO */}
          {step === 0 && (
            <div className="text-center space-y-8">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 bg-[#00d4ff]/10 border border-[#00d4ff]/20 text-[#00d4ff] text-xs font-semibold px-4 py-1.5 rounded-full">
                  <Zap className="w-3.5 h-3.5" /> Respuesta garantizada en 60 segundos
                </div>
                <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight">
                  {STEPS[0].title}
                </h1>
                <p className="text-slate-400 text-lg">{STEPS[0].subtitle}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {STATS.map(({ icon: Icon, value, label }) => (
                  <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-left">
                    <Icon className="w-5 h-5 text-[#00d4ff] mb-2" />
                    <div className="text-2xl font-bold text-white">{value}</div>
                    <div className="text-slate-500 text-sm">{label}</div>
                  </div>
                ))}
              </div>

              <button
                data-testid="quiz-start-btn"
                onClick={() => setStep(1)}
                className="w-full py-4 px-8 bg-[#00d4ff] text-[#030712] font-bold text-lg rounded-2xl hover:bg-[#00bfea] transition-all flex items-center justify-center gap-2 group"
              >
                Comenzar ahora
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-slate-600 text-xs">Sin compromiso · 2 minutos · 100% gratis</p>
            </div>
          )}

          {/* PASO 1 — CONTACTO */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-white">{STEPS[1].title}</h2>
                <p className="text-slate-400 mt-1">{STEPS[1].subtitle}</p>
              </div>
              <div className="space-y-4">
                <input
                  data-testid="quiz-name-input"
                  type="text"
                  placeholder="Tu nombre completo *"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-[#00d4ff]/50 transition-colors"
                />
                <input
                  data-testid="quiz-phone-input"
                  type="tel"
                  placeholder="WhatsApp (+34 6XX XXX XXX) *"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-[#00d4ff]/50 transition-colors"
                />
                <input
                  data-testid="quiz-email-input"
                  type="email"
                  placeholder="Email (opcional)"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-[#00d4ff]/50 transition-colors"
                />
                <input
                  type="text"
                  placeholder="País"
                  value={form.country}
                  onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-[#00d4ff]/50 transition-colors"
                />
              </div>
            </div>
          )}

          {/* PASO 2 — INTERÉS */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-white">{STEPS[2].title}</h2>
                <p className="text-slate-400 mt-1">{STEPS[2].subtitle}</p>
              </div>
              <div className="space-y-3">
                {INTEREST_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    data-testid={`quiz-interest-${opt.value}`}
                    onClick={() => setForm(f => ({ ...f, interest: opt.value }))}
                    className={`w-full p-4 rounded-2xl border text-left transition-all flex items-center gap-4 ${
                      form.interest === opt.value
                        ? 'bg-[#00d4ff]/10 border-[#00d4ff]/50 text-white'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20'
                    }`}
                  >
                    <span className="text-2xl">{opt.emoji}</span>
                    <div>
                      <div className="font-semibold">{opt.label}</div>
                      <div className="text-sm text-slate-500">{opt.desc}</div>
                    </div>
                    {form.interest === opt.value && (
                      <CheckCircle className="w-5 h-5 text-[#00d4ff] ml-auto flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PASO 3 — PRESUPUESTO */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-white">{STEPS[3].title}</h2>
                <p className="text-slate-400 mt-1">{STEPS[3].subtitle}</p>
              </div>
              <div className="space-y-3">
                {BUDGET_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    data-testid={`quiz-budget-${opt.value}`}
                    onClick={() => setForm(f => ({ ...f, budget: opt.value }))}
                    className={`w-full p-4 rounded-2xl border text-left transition-all flex items-center gap-4 ${
                      form.budget === opt.value
                        ? 'bg-[#00d4ff]/10 border-[#00d4ff]/50 text-white'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20'
                    }`}
                  >
                    <span className="text-2xl">{opt.emoji}</span>
                    <div className="font-semibold">{opt.label}</div>
                    {form.budget === opt.value && (
                      <CheckCircle className="w-5 h-5 text-[#00d4ff] ml-auto flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PASO 4 — URGENCIA */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-white">{STEPS[4].title}</h2>
                <p className="text-slate-400 mt-1">{STEPS[4].subtitle}</p>
              </div>
              <div className="space-y-3">
                {URGENCY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    data-testid={`quiz-urgency-${opt.value}`}
                    onClick={() => setForm(f => ({ ...f, urgency: opt.value }))}
                    className={`w-full p-4 rounded-2xl border text-left transition-all flex items-center gap-4 ${
                      form.urgency === opt.value
                        ? 'bg-[#00d4ff]/10 border-[#00d4ff]/50 text-white'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20'
                    }`}
                  >
                    <span className="text-2xl">{opt.emoji}</span>
                    <div className="font-semibold">{opt.label}</div>
                    {form.urgency === opt.value && (
                      <CheckCircle className="w-5 h-5 text-[#00d4ff] ml-auto flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              {error && (
                <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  {error}
                </p>
              )}
            </div>
          )}

          {/* Navigation */}
          {step > 0 && (
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-2 px-5 py-3 bg-white/5 border border-white/10 text-slate-300 rounded-xl hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Atrás
              </button>
              {step < 4 ? (
                <button
                  data-testid="quiz-next-btn"
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canNext()}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-[#00d4ff] text-[#030712] font-bold rounded-xl hover:bg-[#00bfea] disabled:opacity-40 disabled:cursor-not-allowed transition-all group"
                >
                  Siguiente <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <button
                  data-testid="quiz-submit-btn"
                  onClick={handleSubmit}
                  disabled={!canNext() || loading}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-[#00d4ff] text-[#030712] font-bold rounded-xl hover:bg-[#00bfea] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Enviando...
                    </span>
                  ) : (
                    <>Enviar y recibir contacto <Zap className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/5 px-6 py-4 text-center">
        <p className="text-slate-600 text-xs">
          Neuralia369 · Tu información está segura · Sin spam
        </p>
      </div>
    </div>
  );
}
